import { inject, Injectable, Injector, isDevMode, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { SKIP_ACCESS_REDIRECT } from 'src/app/core/auth/interceptors/skip-access-redirect.token';
import { SKIP_AUTH_REDIRECT } from 'src/app/core/auth/interceptors/skip-auth-redirect.token';
import { skipErrorHandlerContext } from 'src/app/core/auth/interceptors/skip-error-handler.token';
import { EnumCatalogueEntryResponseDto } from 'src/app/shared/data/dto/enum-catalogue/enum-catalogue-entry-response-dto.model';
import { EnumCatalogueValueResponseDto } from 'src/app/shared/data/dto/enum-catalogue/enum-catalogue-value-response-dto.model';
import { SelectItem } from 'src/app/shared/data/models/select-item.model';
import { ApiClient } from 'src/app/shared/services/common/api-client/api-client.service';
import { ApiController } from 'src/app/shared/services/common/api-urls/api-controller.enum';
import { apiUrl } from 'src/app/shared/services/common/api-urls/api-urls';

import { findEnumCatalogueDrift } from './enum-catalogue-drift';
import { ENUM_CATALOGUE_ENTRIES } from './enum-catalogue-entries.token';
import { EnumCatalogueName, EnumValue } from './enum-registry';

/** The text shown for a null, unknown or unlabelled value. */
export const ENUM_LABEL_FALLBACK = '-';

const FLAGS_SEPARATOR = ', ';

interface IndexedEnumCatalogueEntry {
	entry: EnumCatalogueEntryResponseDto;
	byValue: ReadonlyMap<number, EnumCatalogueValueResponseDto>;
}

/** Options of `EnumCatalogueService.options`. */
export interface EnumCatalogueOptionsFilter<K extends EnumCatalogueName> {
	/**
	 * Offer only these values; the catalogue order is kept and values the catalogue lacks are left out, so it narrows
	 * option lists only. Stored values become items through `toItems` / `toItem`.
	 */
	only?: readonly EnumValue<K>[];
}

/**
 * The backend enum catalogue (`GET api/EnumCatalogue`): labels and display order of every catalogue enum. It is
 * loaded once before bootstrap and never changes afterwards.
 */
@Injectable({ providedIn: 'root' })
export class EnumCatalogueService {
	// ApiClient is resolved from the injector only when `load()` runs.
	private readonly injector = inject(Injector);
	private readonly url = apiUrl(ApiController.EnumCatalogue);

	private readonly entries = signal(indexEntries(inject(ENUM_CATALOGUE_ENTRIES, { optional: true }) ?? []));

	private readonly failed = signal(false);

	/** Whether the startup load failed for good; the root component then shows the startup error instead of the app. */
	readonly loadFailed = this.failed.asReadonly();

	/**
	 * Fetches the catalogue and keeps it; in dev mode every TS enum mirror that drifted from it is logged. A failed
	 * request shows no error toast and redirects nowhere: the returned observable errors and the caller decides.
	 */
	load(): Observable<void> {
		return this.injector
			.get(ApiClient)
			.get<EnumCatalogueEntryResponseDto[]>(
				this.url,
				skipErrorHandlerContext().set(SKIP_AUTH_REDIRECT, true).set(SKIP_ACCESS_REDIRECT, true)
			)
			.pipe(
				map((entries) => {
					this.entries.set(indexEntries(entries));
					if (isDevMode()) {
						findEnumCatalogueDrift(entries).forEach((problem) => console.error(problem));
					}
				})
			);
	}

	/** Records that the startup load failed for good (`loadFailed`). */
	markLoadFailed(): void {
		this.failed.set(true);
	}

	/**
	 * The label of a value. A flags value that is no member is split into its single-bit members, whose labels are
	 * joined in catalogue order; a null value, an unknown value or bits no single-bit member covers give `'-'`.
	 */
	label<K extends EnumCatalogueName>(name: K, value: EnumValue<K> | null | undefined): string {
		return this.resolveLabel(name, value) ?? ENUM_LABEL_FALLBACK;
	}

	/**
	 * Whether the catalogue labels the value, so `label` reads that label rather than the fallback: `false` for a null
	 * value, an unknown value and a flags value with bits no single-bit member covers; `true` for a member, also one
	 * whose own label is `'-'`.
	 */
	has<K extends EnumCatalogueName>(name: K, value: EnumValue<K> | null | undefined): boolean {
		return this.resolveLabel(name, value) !== undefined;
	}

	/** The genitive label of a member, or `'-'` for a null or unknown value and a member without one. */
	labelGenitive<K extends EnumCatalogueName>(name: K, value: EnumValue<K> | null | undefined): string {
		return this.resolveLabelGenitive(name, value) ?? ENUM_LABEL_FALLBACK;
	}

	/** Whether the catalogue has a genitive label for the value: `false` for a null or unknown value and a member without one. */
	hasGenitive<K extends EnumCatalogueName>(name: K, value: EnumValue<K> | null | undefined): boolean {
		return this.resolveLabelGenitive(name, value) !== undefined;
	}

	/** The select options of an enum in catalogue order; a flags enum leaves out its `0` member. */
	options<K extends EnumCatalogueName>(name: K, filter?: EnumCatalogueOptionsFilter<K>): SelectItem<EnumValue<K>>[] {
		const indexed = this.entries().get(name);
		if (indexed === undefined) {
			return [];
		}

		const only = filter?.only === undefined ? undefined : new Set<number>(filter.only);
		return indexed.entry.values
			.filter((value) => !(indexed.entry.isFlags && value.value === 0))
			.filter((value) => only === undefined || only.has(value.value))
			.map((value) => ({ id: value.value as EnumValue<K>, name: value.label }));
	}

	/**
	 * The labelled items of stored values, one per value in the given order. A value the catalogue lacks keeps its id
	 * and reads `'-'`, so the items carry every value whether or not the catalogue knows it.
	 */
	toItems<K extends EnumCatalogueName>(
		name: K,
		values: readonly EnumValue<K>[] | null | undefined
	): SelectItem<EnumValue<K>>[] {
		return (values ?? []).map((value) => ({ id: value, name: this.label(name, value) }));
	}

	/** The labelled item of a stored value, or `null` for a null value; see `toItems`. */
	toItem<K extends EnumCatalogueName>(
		name: K,
		value: EnumValue<K> | null | undefined
	): SelectItem<EnumValue<K>> | null {
		return value === null || value === undefined ? null : { id: value, name: this.label(name, value) };
	}

	private resolveLabel<K extends EnumCatalogueName>(
		name: K,
		value: EnumValue<K> | null | undefined
	): string | undefined {
		const indexed = this.entries().get(name);
		if (indexed === undefined || value === null || value === undefined) {
			return undefined;
		}

		const member = indexed.byValue.get(value);
		if (member !== undefined) {
			return member.label;
		}
		if (!indexed.entry.isFlags || value <= 0) {
			return undefined;
		}

		const parts = indexed.entry.values.filter((part) => isSingleBit(part.value) && (value & part.value) === part.value);
		const covered = parts.reduce((mask, part) => mask | part.value, 0);
		return covered === value ? parts.map((part) => part.label).join(FLAGS_SEPARATOR) : undefined;
	}

	private resolveLabelGenitive<K extends EnumCatalogueName>(
		name: K,
		value: EnumValue<K> | null | undefined
	): string | undefined {
		return value === null || value === undefined ?
				undefined
			:	this.entries().get(name)?.byValue.get(value)?.labelGenitive;
	}
}

function indexEntries(
	entries: readonly EnumCatalogueEntryResponseDto[]
): ReadonlyMap<string, IndexedEnumCatalogueEntry> {
	return new Map(
		entries.map((entry) => [entry.name, { entry, byValue: new Map(entry.values.map((value) => [value.value, value])) }])
	);
}

function isSingleBit(value: number): boolean {
	return value > 0 && (value & (value - 1)) === 0;
}
